import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRenameAgentFile } from "@/hooks/useAgentFiles";

const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;

const schema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .regex(SLUG_RE, "Must be a URL slug: lowercase letters, numbers, and hyphens only"),
});

type FormValues = z.infer<typeof schema>;

interface RenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileId: string;
  currentName: string;
}

export function RenameDialog({ open, onOpenChange, fileId, currentName }: RenameDialogProps) {
  const renameFile = useRenameAgentFile();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: currentName },
  });

  // Reset to the current name whenever the dialog opens with a new file.
  useEffect(() => {
    if (open) {
      form.reset({ name: currentName });
    }
  }, [open, currentName, form]);

  const onSubmit = async (values: FormValues) => {
    await renameFile.mutateAsync({ id: fileId, name: values.name });
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) form.reset({ name: currentName });
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input className="font-mono" autoFocus {...field} />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    URL slug — lowercase letters, numbers, and hyphens.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={renameFile.isPending}>
                {renameFile.isPending ? "Saving…" : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
